/**
 * Fixes applied to a camera's offer before the browser sees it.
 *
 * Kept free of React and of `window` so it can be checked without a renderer.
 */

/**
 * Mark plain H.264 Baseline as Constrained Baseline.
 *
 * SV-IPC8MZ offers `profile-level-id=42001e` — Baseline, no constraint flags. Chrome on
 * Android decodes H.264 through the hardware decoder, which advertises only
 * Constrained Baseline (`42e0xx`) and High, so it finds no common codec, answers the
 * video section with port 0, and that rejection takes the bundled transport with it:
 * the camera's candidates are accepted and thrown away, ICE never leaves `new`, and the
 * player sits on an empty picture. Desktop Chrome lists plain Baseline too, which is
 * why the same camera played there (found 2026-10-01).
 *
 * What separates Baseline from Constrained Baseline is FMO, ASO and redundant slices —
 * tools no camera encoder uses — so the stream already IS Constrained Baseline and
 * saying so is a label fix, not a transcode. The camera's own offer should say it;
 * until its firmware does, this does.
 *
 * Only profile_idc 0x42 is touched, only by setting constraint_set1 (with 0 and 2, the
 * `e0` everyone sends); the level byte is kept.
 */
export function constrainBaselineH264(sdp: string): string {
  return sdp.replace(
    /profile-level-id=42([0-9a-fA-F]{2})([0-9a-fA-F]{2})/g,
    (whole, constraints: string, level: string) => {
      const flags = parseInt(constraints, 16);
      // constraint_set1 already set: Constrained Baseline as offered.
      if (Math.floor(flags / 0x40) % 2 === 1) {
        return whole;
      }
      return `profile-level-id=42e0${level}`;
    },
  );
}
